/**
 * DisasterFAB.js — Floating Action Button for live disaster predictions
 *
 * A 52×52px circle fixed at bottom-right, always visible on every page.
 * Replaces the old DisasterIntegrationPanel panel that covered screen area.
 *
 * Behaviour:
 *   • Quiet (0 events)   → small teal dot, no badge
 *   • Watch/Warning      → orange, badge count
 *   • Critical           → red, pulse ring, auto-expands drawer once
 *   • Click FAB          → toggles slide-up drawer
 *   • Drawer idle 10s    → auto-collapses
 *   • Click outside FAB  → collapses drawer
 *   • Data polls every 120s via useEffect cleanup (no module-level interval)
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { API_URLS } from '../../config';

// ── Uganda fallback data ──────────────────────────────────────────────────────

const now = Date.now();
const h = (n) => new Date(now + n * 3_600_000).toISOString();

const FALLBACK_DISASTERS = [
  {
    id: 'ug-w-001', category: 'weather', type: 'flood', severity: 'critical',
    ugandaRegion: 'Central', title: 'Urban Flash Flood — Kampala Basin',
    onset: h(3), expires: h(24), confidence: 89,
    plain_impact: 'Heavy flooding expected in Bwaise, Kamwokya and Katanga. Do not cross flooded roads.',
    source_model: 'TEC-Precip Fusion v2',
  },
  {
    id: 'ug-w-002', category: 'weather', type: 'landslide', severity: 'critical',
    ugandaRegion: 'Eastern', title: 'Landslide Risk — Mt. Elgon / Bududa',
    onset: h(6), expires: h(36), confidence: 84,
    plain_impact: 'Dangerous landslides likely in Bududa. Leave steep hillsides now.',
    source_model: 'TEC-Precip Fusion v2',
  },
  {
    id: 'ug-s-000', category: 'space', type: 'plasma_bubble', severity: 'warning',
    ugandaRegion: 'Central', title: 'Plasma Bubble — Post-Sunset GPS Disruption',
    onset: h(5), expires: h(14), confidence: 83,
    activeWindow: '20:00–02:00 EAT',
    plain_impact: 'GPS apps will show wrong locations tonight. A natural space weather event.',
    source_model: 'Busitema Stacked Ensemble (XGBoost + LSTM + ANN) v3.1',
  },
  {
    id: 'ug-s-002', category: 'space', type: 'geomagnetic_storm', severity: 'critical',
    ugandaRegion: 'Central', title: 'Geomagnetic Storm — Power Grid Risk',
    onset: h(1), expires: h(14), confidence: 91,
    plain_impact: 'Power cuts expected across Uganda. Switch hospitals and critical infrastructure to generators.',
    source_model: 'NOAA SWPC / Busitema Space Weather Lab',
  },
  {
    id: 'ug-w-003', category: 'weather', type: 'drought', severity: 'watch',
    ugandaRegion: 'Karamoja', title: 'Drought Watch — Karamoja Sub-region',
    onset: h(72), expires: h(7 * 24), confidence: 76,
    plain_impact: 'Water sources drying up in Karamoja. Register for emergency food/water assistance.',
    source_model: 'Soil Moisture Anomaly Detector v1',
  },
];

// ── Visual helpers ────────────────────────────────────────────────────────────

const TYPE_ICON = {
  flood: '🌊', drought: '☀️', landslide: '⛰️', storm: '⛈️', heatwave: '🌡️',
  plasma_bubble: '🫧', tec_enhancement: '📶', ionospheric_storm: '🌩️',
  equatorial_spread_f: '〰️', gps_disruption: '📡', geomagnetic_storm: '🌐',
  solar_radio_burst: '📻', solar_energetic_particles: '☢️',
};

const WEATHER_COLOR = { watch: '#f59e0b', warning: '#f97316', critical: '#ef4444' };
const SPACE_COLOR   = { watch: '#3b82f6', warning: '#8b5cf6', critical: '#ec4899' };

function getColor(d) {
  return d.category === 'space' ? SPACE_COLOR[d.severity] : WEATHER_COLOR[d.severity];
}

function sortBySeverity(a, b) {
  const rank = { critical: 0, warning: 1, watch: 2 };
  const r = rank[a.severity] - rank[b.severity];
  return r !== 0 ? r : new Date(a.onset) - new Date(b.onset);
}

function countdown(iso) {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return 'Ongoing';
  const hrs = Math.floor(ms / 3_600_000);
  const min = Math.floor((ms % 3_600_000) / 60_000);
  return hrs === 0 ? `in ${min}m` : `in ${hrs}h ${min}m`;
}

// ── Auto-collapse hook ────────────────────────────────────────────────────────

function useIdleCollapse(open, setOpen, delayMs = 10_000) {
  const timerRef = useRef(null);

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (open) {
      timerRef.current = setTimeout(() => setOpen(false), delayMs);
    }
  }, [open, setOpen, delayMs]);

  useEffect(() => {
    reset();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [reset]);

  return reset; // call on any interaction inside the drawer
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DisasterFAB() {
  const [disasters, setDisasters] = useState(FALLBACK_DISASTERS);
  const [open,      setOpen]      = useState(false);
  const [loading,   setLoading]   = useState(false);
  // Track whether we've already auto-opened for the current critical batch
  const autoOpenedRef = useRef(false);

  // ── Data fetch + poll ────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      try {
        const [weatherRes, spaceRes] = await Promise.allSettled([
          fetch(API_URLS.weatherDisasters, { signal: AbortSignal.timeout(8_000), cache: 'no-store' })
            .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
            .then((d) => Array.isArray(d) ? d : (d?.data ?? [])),
          fetch(API_URLS.spaceDisasters, { signal: AbortSignal.timeout(8_000), cache: 'no-store' })
            .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
            .then((d) => Array.isArray(d) ? d : (d?.data ?? [])),
        ]);

        const weather = weatherRes.status === 'fulfilled'
          ? weatherRes.value
          : FALLBACK_DISASTERS.filter((d) => d.category === 'weather');
        const space = spaceRes.status === 'fulfilled'
          ? spaceRes.value
          : FALLBACK_DISASTERS.filter((d) => d.category === 'space');

        if (alive) setDisasters([...weather, ...space]);
      } catch { /* silently use fallback */ }
      finally { if (alive) setLoading(false); }
    }

    load();
    const id = setInterval(load, 120_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ── Auto-expand once on first critical event ─────────────────────────────
  useEffect(() => {
    const hasCritical = disasters.some((d) => d.severity === 'critical');
    if (hasCritical && !autoOpenedRef.current) {
      setOpen(true);
      autoOpenedRef.current = true;
    }
    if (!hasCritical) {
      autoOpenedRef.current = false; // reset so next critical batch auto-opens
    }
  }, [disasters]);

  // ── Derived counts ───────────────────────────────────────────────────────
  const total    = disasters.length;
  const critical = disasters.filter((d) => d.severity === 'critical').length;
  const top3     = useMemo(() => [...disasters].sort(sortBySeverity).slice(0, 3), [disasters]);

  const hasCritical = critical > 0;
  const hasAny      = total > 0;

  // FAB color: red → critical, orange → any active, teal → quiet
  const fabColor = hasCritical ? '#ef4444' : hasAny ? '#f97316' : '#0ea5e9';
  const fabBg    = hasCritical ? 'rgba(239,68,68,0.18)'
                : hasAny       ? 'rgba(249,115,22,0.16)'
                :                'rgba(14,165,233,0.12)';

  // ── Idle auto-collapse ───────────────────────────────────────────────────
  const resetIdle = useIdleCollapse(open, setOpen, 10_000);

  // ── Close on outside click ───────────────────────────────────────────────
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      onMouseMove={resetIdle}
      style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 1200, fontFamily: "'Outfit','DM Sans',sans-serif" }}
    >

      {/* ── Slide-up drawer ─────────────────────────────────────────────── */}
      <div
        style={{
          position:      'absolute',
          bottom:        '64px',
          right:         0,
          width:         '310px',
          maxHeight:     '420px',
          overflowY:     'auto',
          borderRadius:  '14px',
          background:    'var(--bg-surface, #0f1e38)',
          border:        `1px solid ${hasCritical ? 'rgba(239,68,68,0.4)' : 'rgba(56,189,248,0.25)'}`,
          borderLeft:    `3px solid ${fabColor}`,
          boxShadow:     '0 12px 40px rgba(0,0,0,0.5)',
          transformOrigin: 'bottom right',
          transform:     open ? 'scale(1) translateY(0)'   : 'scale(0.92) translateY(12px)',
          opacity:       open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition:    'transform 0.22s cubic-bezier(0.34,1.56,0.64,1), opacity 0.18s ease',
          scrollbarWidth: 'none',
        }}
      >
        {/* Drawer header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '12px 14px 8px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          {hasCritical && (
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
              background: '#ef4444', boxShadow: '0 0 6px #ef4444',
              animation: 'fab-blink 1.1s ease-in-out infinite',
            }} />
          )}
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary, #f0f6ff)', flex: 1 }}>
            ⚠️ Active Predictions
          </span>
          {critical > 0 && (
            <span style={{
              fontSize: '10px', fontWeight: 700, color: '#ef4444',
              background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: '4px', padding: '2px 7px',
            }}>{critical} critical</span>
          )}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close disaster predictions"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted, #7096b8)', fontSize: '16px', lineHeight: 1,
              padding: '2px 4px', borderRadius: '4px',
            }}
          >×</button>
        </div>

        {/* Event cards */}
        <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '7px' }}>

          {loading && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted, #7096b8)', textAlign: 'center', padding: '6px 0' }}>
              Updating predictions…
            </div>
          )}

          {!loading && top3.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted, #7096b8)', textAlign: 'center', padding: '12px 0' }}>
              🌤 No active predictions right now
            </div>
          )}

          {top3.map((d) => {
            const c = getColor(d);
            return (
              <a
                key={d.id}
                href={`${API_URLS.weatherApp}?disaster=${d.id}`}
                target="_blank"
                rel="noreferrer"
                onClick={resetIdle}
                style={{ textDecoration: 'none' }}
              >
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '9px',
                  padding: '9px 11px', borderRadius: '8px',
                  background: `${c}0e`, border: `1px solid ${c}28`,
                  borderLeft: `3px solid ${c}`,
                  transition: 'background 0.15s',
                }}>
                  <span style={{
                    fontSize: '15px', flexShrink: 0, marginTop: '2px',
                    filter: d.category === 'space' ? `drop-shadow(0 0 4px ${c})` : undefined,
                  }}>
                    {TYPE_ICON[d.type] ?? '⚠️'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Severity + countdown + confidence */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                      <span style={{
                        fontSize: '9px', fontWeight: 700, color: c,
                        background: `${c}20`, padding: '1px 5px', borderRadius: '3px',
                        letterSpacing: '0.06em',
                      }}>{d.severity.toUpperCase()}</span>
                      <span style={{ fontSize: '9px', color: 'var(--text-muted, #7096b8)', marginLeft: 'auto' }}>
                        {countdown(d.onset)}
                      </span>
                      <span style={{ fontSize: '9px', color: 'var(--text-muted, #7096b8)' }}>
                        {d.confidence}%
                      </span>
                    </div>

                    {/* Plain-language impact (primary) */}
                    {d.plain_impact ? (
                      <div style={{
                        fontSize: '11px', fontWeight: 600, color: 'var(--text-primary, #f0f6ff)',
                        lineHeight: 1.4,
                        display: '-webkit-box', WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {d.plain_impact}
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '11px', fontWeight: 600, color: 'var(--text-primary, #f0f6ff)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {d.title.includes('—') ? d.title.split('—')[0].trim() : d.title}
                      </div>
                    )}

                    {/* Active window for nighttime events */}
                    {d.activeWindow && (
                      <div style={{ fontSize: '9px', color: '#38bdf8', marginTop: '2px' }}>
                        ⏱ {d.activeWindow}
                      </div>
                    )}

                    {/* Region */}
                    <div style={{ fontSize: '9px', color: 'var(--text-muted, #7096b8)', marginTop: '2px' }}>
                      {d.ugandaRegion} · {d.source_model?.split('/')[0].trim()}
                    </div>
                  </div>
                </div>
              </a>
            );
          })}

          {/* CTA */}
          <a
            href={API_URLS.weatherApp}
            target="_blank"
            rel="noreferrer"
            onClick={resetIdle}
            style={{
              display: 'block', textAlign: 'center', textDecoration: 'none',
              padding: '9px 0', marginTop: '2px',
              borderRadius: '7px', fontSize: '12px', fontWeight: 700,
              minHeight: '44px', lineHeight: '26px',
              background: hasCritical ? 'rgba(239,68,68,0.13)' : 'rgba(56,189,248,0.11)',
              border: `1px solid ${hasCritical ? 'rgba(239,68,68,0.35)' : 'rgba(56,189,248,0.28)'}`,
              color: hasCritical ? '#ef4444' : '#38bdf8',
              transition: 'background 0.15s',
            }}
          >
            🗺 View All on Disaster Map →
          </a>

          <div style={{ fontSize: '9px', color: 'var(--text-muted, #7096b8)', textAlign: 'center', paddingBottom: '2px' }}>
            UNMA · Busitema University Space Weather Lab
          </div>
        </div>
      </div>

      {/* ── FAB circle ──────────────────────────────────────────────────── */}
      <button
        onClick={() => { setOpen((s) => !s); resetIdle(); }}
        aria-label={`${total} active disaster prediction${total !== 1 ? 's' : ''}. Click to ${open ? 'close' : 'expand'}.`}
        aria-expanded={open}
        style={{
          position:      'relative',
          width:         '52px',
          height:        '52px',
          borderRadius:  '50%',
          background:    fabBg,
          border:        `2px solid ${fabColor}`,
          boxShadow:     `0 4px 20px rgba(0,0,0,0.4), 0 0 0 0 ${fabColor}`,
          cursor:        'pointer',
          display:       'flex',
          alignItems:    'center',
          justifyContent: 'center',
          fontSize:      '22px',
          transition:    'border-color 0.3s, box-shadow 0.3s, transform 0.15s',
          transform:     open ? 'scale(0.93)' : 'scale(1)',
          animation:     hasCritical ? 'fab-pulse-ring 2s ease-out infinite' : undefined,
        }}
      >
        {/* Icon */}
        <span style={{ filter: hasCritical ? `drop-shadow(0 0 6px ${fabColor})` : undefined }}>
          {hasCritical ? '⚠️' : hasAny ? '🔔' : '✅'}
        </span>

        {/* Badge */}
        {total > 0 && (
          <span style={{
            position:     'absolute',
            top:          '-4px',
            right:        '-4px',
            minWidth:     '20px',
            height:       '20px',
            borderRadius: '10px',
            background:   fabColor,
            color:        '#fff',
            fontSize:     '11px',
            fontWeight:   800,
            lineHeight:   '20px',
            textAlign:    'center',
            padding:      '0 4px',
            border:       '2px solid var(--bg-surface, #0f1e38)',
            boxShadow:    `0 2px 8px rgba(0,0,0,0.4)`,
          }}>
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {/* Keyframes */}
      <style>{`
        @keyframes fab-blink {
          0%,100% { opacity: 1; box-shadow: 0 0 6px #ef4444; }
          50%      { opacity: 0.35; box-shadow: 0 0 2px #ef4444; }
        }
        @keyframes fab-pulse-ring {
          0%   { box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 0 0 0 rgba(239,68,68,0.55); }
          70%  { box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 0 0 14px rgba(239,68,68,0); }
          100% { box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 0 0 0 rgba(239,68,68,0); }
        }
      `}</style>
    </div>
  );
}
